import { Trade } from "../../api";

interface Props {
  trades: Trade[];
}

function clockTime(iso: string | null): string {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return "—";
  }
}

function statusTone(status: Trade["status"]): {
  glyph: string;
  bg: string;
  text: string;
} {
  switch (status) {
    case "filled":
      return { glyph: "✓", bg: "bg-success/15", text: "text-success" };
    case "closed":
      return { glyph: "■", bg: "bg-fg-muted/15", text: "text-fg-muted" };
    case "rejected":
    case "skipped":
      return { glyph: "✕", bg: "bg-danger/15", text: "text-danger" };
    case "pending":
      return { glyph: "…", bg: "bg-warn/15", text: "text-warn" };
    default:
      return { glyph: "?", bg: "bg-fg-muted/15", text: "text-fg-muted" };
  }
}

function fmtPnl(v: number | null): string {
  if (v == null) return "—";
  return `${v >= 0 ? "+" : ""}${v.toFixed(2)}`;
}

export function TradeFeed({ trades }: Props) {
  return (
    <section className="flex h-full flex-col rounded-xl border border-border bg-surface">
      <header className="flex items-baseline justify-between border-b border-border px-5 py-3">
        <h3 className="text-[11px] font-medium uppercase tracking-[0.15em] text-fg-muted">
          Recent trades
        </h3>
        <span className="font-mono text-xs text-fg-muted">
          last {trades.length}
        </span>
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto">
        {trades.length === 0 ? (
          <p className="p-5 text-sm text-fg-muted">no trades yet</p>
        ) : (
          <ul className="divide-y divide-border/60">
            {trades.map((t) => {
              const tone = statusTone(t.status);
              const when = clockTime(t.entry_time ?? t.created_at);
              const pnl = t.pnl_currency;
              return (
                <li
                  key={t.id}
                  className="flex items-center gap-3 px-5 py-2.5 transition-colors hover:bg-bg-hover/50"
                >
                  <span
                    className={`flex h-6 w-6 shrink-0 items-center justify-center rounded font-mono text-xs font-bold ${tone.bg} ${tone.text}`}
                    title={t.status}
                  >
                    {tone.glyph}
                  </span>
                  <span className="font-mono text-xs text-fg-muted">{when}</span>
                  <span className="font-mono text-sm font-semibold">{t.symbol}</span>
                  <span
                    className={`rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${
                      t.direction === "buy"
                        ? "bg-success/15 text-success"
                        : "bg-danger/15 text-danger"
                    }`}
                  >
                    {t.direction}
                  </span>
                  <span className="ml-auto font-mono text-sm tabular-nums">
                    <span
                      className={
                        pnl == null
                          ? "text-fg-muted"
                          : pnl > 0
                            ? "text-success"
                            : pnl < 0
                              ? "text-danger"
                              : ""
                      }
                    >
                      {t.status === "closed" ? fmtPnl(pnl) : "—"}
                    </span>
                  </span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
